import React, { useRef, useCallback, useState } from 'react';
import {
  useAssessmentApi,
  type ASRResult,
  type AssessmentNextStepResponse,
  type StartAssessmentResponse,
} from './useAssessmentApi';
import {
  toResumeOffer,
  ResumeExpiredError,
  type ResumeOffer,
} from './useLessonApi';
import { useLessonState, type Message } from './useLessonState';
import { useAutoContinue } from './useAutoContinue';
import {
  isAbortError,
  applyHandleNextStepError,
  runAutoContinueWithErrorClearing,
} from '../utils/lessonFlowUtils';
import {
  handleProvideIntroductionStep,
  handleRequestProductionStep,
  handleGiveFeedbackStep,
  handleExplainMeaningStep,
  handleFallbackStep,
  awaitsLearnerInput,
  stepMeta,
  type Step,
} from '../utils/stepHandlers';

export const useAssessmentFlow = (
  contextId: string | undefined,
  state: ReturnType<typeof useLessonState>,
  api: ReturnType<typeof useAssessmentApi>,
  autoContinue: ReturnType<typeof useAutoContinue>
): {
  /**
   * Opens the check-in: offers a resumable run when there is one, else starts. `skipResumeCheck`
   * starts a fresh run without asking (Restart).
   */
  startAssessment: (
    signal?: AbortSignal,
    bypassGuard?: boolean,
    skipResumeCheck?: boolean
  ) => Promise<void>;
  /** The unfinished check-in the learner is being offered, until they pick Continue or Restart. */
  resumeOffer: ResumeOffer | null;
  /** The run on screen was resumed, so its intro was already seen. */
  resumed: boolean;
  /** Continue the offered run; falls back to a fresh start when the window has passed. */
  continueRun: (signal?: AbortSignal) => Promise<void>;
  /** Start a fresh run instead of the offered one (the server abandons the old run). */
  restartRun: (signal?: AbortSignal) => Promise<void>;
  handleNextStep: (
    runId: string,
    stepVersion: number,
    asrResult?: ASRResult
  ) => Promise<boolean>;
  abortControllerRef: React.MutableRefObject<AbortController | null>;
} => {
  const abortControllerRef = useRef<AbortController | null>(null);
  const [resumeOffer, setResumeOffer] = useState<ResumeOffer | null>(null);
  const [resumed, setResumed] = useState(false);

  const handleNextStepRef = useRef<
    (runId: string, stepVersion: number, asr?: ASRResult) => Promise<boolean>
  >(async () => false);

  // Shows a step the server returned (from /next, or a resumed run's current prompt).
  const renderStep = useCallback(
    async (
      currentRunId: string,
      data: AssessmentNextStepResponse
    ): Promise<void> => {
      const handleNextStep = handleNextStepRef.current;
      state.setStepVersion(data.step_version);
      const nextStepIndex = (data.step as Step).step_index;
      if (nextStepIndex != null) state.setStepIndex(nextStepIndex);

      const actionType = data.step.action_type;
      const stepId = `step-${(data.step as Step).step_index ?? 0}-${actionType}-${Date.now()}`;
      // Mic off now; on only once this step's messages are in.
      const awaitsInput = awaitsLearnerInput(data.step as Step);
      if (!awaitsInput) state.setAwaitingInput(false);

      // Check-in persona lines are audio only (BR-043): no target-language
      // text is rendered and only natural-speed audio is offered. The text
      // is kept in audioText so a failed clip can be re-fetched.
      const personaAudioMessage = async (
        text: string,
        locale: string,
        speed: 'natural' | 'slow' = 'natural'
      ): Promise<Message> => {
        // Propagate locale as the language code AssessmentRunner passes to the
        // mic for speech recognition.
        if (locale) {
          state.setLanguageCode(locale);
        }

        const message: Message = {
          id: stepId,
          sender: 'tutor',
          content: '',
          audioOnly: true,
          audioText: text || undefined,
          audioLocale: locale || undefined,
          audioSpeed: speed,
          autoPlay: true,
          timestamp: new Date().toISOString(),
          // A persona line is a play_audio card whichever step carried it (the re-prompt rides a
          // request_production step).
          ...stepMeta(data.step as Step),
          stepKind: 'play_audio',
        };

        if (locale && text) {
          try {
            message.audioUrls = {
              natural: await api.fetchTextAudioUrl(
                currentRunId,
                text,
                locale,
                speed,
                abortControllerRef.current?.signal
              ),
            };
          } catch (audioErr) {
            console.warn('[useAssessmentFlow] Audio fetch failed:', audioErr);
            // The card offers a retry; the text is never shown instead.
            message.audioUnavailable = 'fetch_failed';
          }
        } else {
          message.audioUnavailable = 'missing';
        }
        return message;
      };

      const help = (data.step.content?.help ?? {}) as Record<string, unknown>;
      // Support for a part the learner keeps failing (BR-035): the
      // translation, context and phrase to say, as text after the line.
      const helpMessages = (): Message[] => {
        const lines: Array<[string, string]> = [];
        if (typeof help.translation === 'string' && help.translation) {
          lines.push(['translation', `They said: ${help.translation}`]);
        }
        if (typeof help.context === 'string' && help.context) {
          lines.push(['context', help.context]);
        }
        const phrases = Array.isArray(help.expected_phrases)
          ? (help.expected_phrases as string[])
          : [];
        if (phrases.length > 0) {
          lines.push(['phrase', `You can say: ${phrases.join(' / ')}`]);
        }
        return lines.map(([kind, content]) => ({
          id: `${stepId}-help-${kind}`,
          sender: 'tutor' as const,
          stepKind: 'help',
          content,
          timestamp: new Date().toISOString(),
        }));
      };

      if (actionType === 'play_audio') {
        const message = await personaAudioMessage(
          (data.step.content?.text as string) ?? '',
          (data.step.content?.locale as string) ?? '',
          help.speed === 'slow' ? 'slow' : 'natural'
        );

        state.setMessagesWithCheck((prev) => [
          ...prev,
          message,
          ...helpMessages(),
        ]);

        if (!data.completed) {
          await new Promise((r) => setTimeout(r, 0));
          await runAutoContinueWithErrorClearing({
            runId: currentRunId,
            nextStepVersion: data.step_version,
            state,
            autoContinue,
            handleNextStep,
            signal: abortControllerRef.current?.signal,
            logLabel: 'Error auto-continuing after play_audio:',
          });
        }
      } else if (
        actionType === 'request_production' &&
        data.step.content?.out_of_context
      ) {
        // The character's in-character re-prompt is a spoken line too (BR-043, BR-035).
        const message = await personaAudioMessage(
          (data.step.content?.prompt as string) ?? '',
          (data.step.content?.locale as string) ?? ''
        );
        state.setMessagesWithCheck((prev) => [...prev, message]);
      } else if (actionType === 'request_production') {
        const result = handleRequestProductionStep(data.step as Step, stepId);
        state.setMessagesWithCheck((prev) => [
          ...prev,
          ...(result.messages ?? []),
          ...helpMessages(),
        ]);
      } else if (actionType === 'give_feedback') {
        // give_feedback is not expected from the assessment API, but handled defensively.
        const result = await handleGiveFeedbackStep(data.step as Step, stepId);
        if (result.messages) {
          state.setMessagesWithCheck((prev) => [...prev, ...result.messages!]);
        }
        if (result.needsAutoContinue && currentRunId && data.step_version) {
          await runAutoContinueWithErrorClearing({
            runId: currentRunId,
            nextStepVersion: data.step_version,
            state,
            autoContinue,
            handleNextStep,
            signal: abortControllerRef.current?.signal,
            logLabel: 'Error auto-continuing after give_feedback:',
          });
        }
      } else if (actionType === 'explain_meaning') {
        const result = handleExplainMeaningStep(data.step as Step, stepId);
        if (result.messages) {
          state.setMessagesWithCheck((prev) => [...prev, ...result.messages!]);
        }
        if (result.needsAutoContinue && currentRunId && data.step_version) {
          await runAutoContinueWithErrorClearing({
            runId: currentRunId,
            nextStepVersion: data.step_version,
            state,
            autoContinue,
            handleNextStep,
            signal: abortControllerRef.current?.signal,
            logLabel: 'Error auto-continuing after explain_meaning:',
          });
        }
      } else if (actionType === 'conversation_complete') {
        // The script ended on the learner's line: nothing to say or show, the completion below takes over.
      } else {
        // provide_context is never returned by /assessments/{runId}/next
        const result = handleFallbackStep(data.step as Step, stepId);
        if (result.messages) {
          state.setMessagesWithCheck((prev) => [...prev, ...result.messages!]);
        }
      }

      if (awaitsInput) state.setAwaitingInput(true);

      // Process step first, then set completion — no early return like useLessonFlow
      if (data.completed) {
        state.setAwaitingInput(false);
        state.setLessonComplete(true);
      }
    },
    [api, state, autoContinue]
  );

  const handleNextStep = useCallback(
    async (
      currentRunId: string,
      currentStepVersion: number,
      asrResult?: ASRResult
    ): Promise<boolean> => {
      if (state.isAdvancingRef.current) {
        return false;
      }

      state.isAdvancingRef.current = true;
      state.setAdvanceFailed(false);
      state.setProcessing(true);

      try {
        const data = await api.nextAssessmentStep(
          currentRunId,
          currentStepVersion,
          asrResult,
          abortControllerRef.current?.signal
        );

        await renderStep(currentRunId, data);
        return true;
      } catch (err) {
        if (isAbortError(err)) {
          return false;
        }
        // A failed answer leaves the learner on the same prompt: mic back on.
        if (asrResult) state.setAwaitingInput(true);
        applyHandleNextStepError(err, state);
        return false;
      } finally {
        state.isAdvancingRef.current = false;
        state.setProcessing(false);
      }
    },
    [api, state, autoContinue, renderStep]
  );
  handleNextStepRef.current = handleNextStep;

  // Runs a start or resume response through the same handling: it is the next step to show either way.
  const beginRun = useCallback(
    async (
      fetchRun: () => Promise<StartAssessmentResponse>,
      signal?: AbortSignal
    ): Promise<void> => {
      let resumedRun = false;
      state.hasStartedRef.current = true;

      try {
        state.setLoading(true);
        state.setError(null);
        setResumeOffer(null);

        const data = await fetchRun();
        resumedRun = data.recap !== undefined;

        state.setRunId(data.run_id);
        state.setStepVersion(data.step_version);
        state.setContextName(data.context_name);
        state.setContextId(data.context_id);
        state.setParts(data.parts ?? []);
        // provide_introduction has no step_index — use ?? 0
        state.setStepIndex(data.step.step_index ?? 0);

        const actionType = data.step.action_type;
        const stepId = `step-${data.step.step_index ?? 0}-${actionType}-${Date.now()}`;
        // Mic off now; on only once this step's messages are in.
        const awaitsInput = awaitsLearnerInput(data.step as Step);
        if (!awaitsInput) state.setAwaitingInput(false);

        if (actionType === 'provide_introduction') {
          const result = handleProvideIntroductionStep(
            data.step as Step,
            stepId
          );
          if (result.messages) {
            state.setMessagesWithCheck((prev) => [
              ...prev,
              ...result.messages!,
            ]);
          }
          if (result.needsAutoContinue && data.run_id) {
            await runAutoContinueWithErrorClearing({
              runId: data.run_id,
              nextStepVersion: data.step_version,
              state,
              autoContinue,
              handleNextStep,
              signal: abortControllerRef.current?.signal,
              logLabel: 'Error auto-continuing after provide_introduction:',
            });
          }
        } else if (resumedRun) {
          // A resumed run's current prompt is rendered like a /next step.
          await renderStep(data.run_id, data);
          return;
        } else {
          const result = handleFallbackStep(data.step as Step, stepId);
          if (result.messages) {
            state.setMessagesWithCheck((prev) => [
              ...prev,
              ...result.messages!,
            ]);
          }
        }
        if (awaitsInput) state.setAwaitingInput(true);
      } catch (err) {
        if (isAbortError(err)) {
          return;
        }
        console.error('[useAssessmentFlow] Failed to start assessment:', err);
        state.setError(
          err instanceof Error ? err.message : 'Failed to start assessment'
        );
      } finally {
        // setLoading(false) unconditionally — unlike useLessonFlow which gates on
        // willAutoContinueRef. LessonRunnerView gates on loading alone, so we must
        // clear it here or the loading spinner permanently overlays the chat.
        if (!signal?.aborted) {
          state.setLoading(false);
        }
      }
    },
    [api, state, autoContinue, handleNextStep, renderStep]
  );

  const startAssessment = useCallback(
    async (
      signal?: AbortSignal,
      bypassGuard = false,
      skipResumeCheck = false
    ): Promise<void> => {
      if (!contextId) {
        state.setError('Context ID is required');
        state.setLoading(false);
        return;
      }

      if (!bypassGuard && state.hasStartedRef.current) {
        return;
      }

      state.hasStartedRef.current = true;
      setResumed(false);

      if (!skipResumeCheck) {
        state.setLoading(true);
        state.setError(null);
        setResumeOffer(null);
        let offer: ResumeOffer | null = null;
        try {
          offer = toResumeOffer(await api.getResumable(contextId, signal));
        } catch (err) {
          // The check is a convenience: if it fails, the check-in just starts.
          if (!isAbortError(err)) {
            console.warn('[useAssessmentFlow] Resumable check failed:', err);
          }
        }
        if (signal?.aborted) return;
        if (offer) {
          setResumeOffer(offer);
          state.setLoading(false);
          return;
        }
      }

      await beginRun(() => api.startAssessment(contextId, signal), signal);
    },
    [contextId, api, state, beginRun]
  );

  const continueRun = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      const offer = resumeOffer;
      if (!offer) return;
      await beginRun(async () => {
        try {
          const data = await api.resumeRun(offer.run_id, signal);
          setResumed(true);
          return data;
        } catch (err) {
          if (!(err instanceof ResumeExpiredError)) throw err;
          // Past the window: start over without asking again.
          return api.startAssessment(contextId ?? '', signal);
        }
      }, signal);
    },
    [resumeOffer, contextId, api, beginRun]
  );

  const restartRun = useCallback(
    (signal?: AbortSignal): Promise<void> =>
      startAssessment(signal, true, true),
    [startAssessment]
  );

  return {
    startAssessment,
    resumeOffer,
    resumed,
    continueRun,
    restartRun,
    handleNextStep,
    abortControllerRef,
  };
};
